import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Divider, Grid, Paper, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
// import { useHistory } from 'react-router';

import { useCompanyApi, useProjectApi } from 'src/services';
import { WebContext } from 'src/scenes/web-context';
import { useFetchStatus } from 'src/hooks';
import { TitleContainer, ContentContainer, MapWrapper, DialogWrap, DialogExternalImage } from 'src/components';
import { getRandomNviroColor } from 'src/utils/util';
import { AnalyticCard, NumberIndicator, SpatialFootprintTable } from 'src/scenes/Project/components';
import { ConcernLevelsStateOfNature }
  from 'src/scenes/CompanyPressuresAndDependencies/components';
import { LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';
import { buildLeafletLayer } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { formatNumber } from 'src/utils/formatters';


const heightVH = n => document.documentElement.clientHeight * (n / 100);

const mapOptions = {
  center: [ -32.48431324745671, -71.25296320202341 ],
  zoomControl: true,
  dragging: true,
  zoom: 16,
  doubleClickZoom: false,
  attributionControl: false,
};

const useStyles = makeStyles(theme => ({
  mainContainer: {
    width: theme.spacing(180),
  },
  title: {
    fontSize: theme.typography.subtitle1.fontSize,
    color: ({ variant }) => (
      variant === 'light' ? theme.palette.common.gray600
      : variant === 'dark' ? theme.palette.common.lightGray
      : theme.palette.common.gray600
    ),
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  map: {
    marginTop: theme.spacing(0.5),
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
  paper: {
    padding: theme.spacing(2),
  },
  number: {
    width: ({ isLoading }) => isLoading ? '40%' : '100%',
    fontSize: theme.typography.h5.fontSize,
    fontWeight: 600,
    color: theme.palette.common.black,
  },

  auditableUnitLabel: {
    backgroundColor: '#F7931E !important',
  },
  itemRootBorder: {
    borderRight: `1px solid ${theme.palette.common.gray}`,
  },
}));

const CompanySummaryContainer = () => {

  const { currentCompany: { companyId, companyName } } = useContext(WebContext);
  const mapRef = useRef();
  // const history = useHistory();
  const companyApi = useCompanyApi();
  const projectApi = useProjectApi();

  const [ indicators, setIndicators ] = useState({
    siteCount: 0,
    projectCount: 0,
    sitesSurface: 0,
    projectSurface: 0,
    minConditionProjects: { ajustedArea: 0, variation: 0, comparedTo: 2020 },
    minConditionSites: { ajustedArea: 0, variation: 0, comparedTo: 2020 },
  });
  // const [ latestProjects, setLatestProjects ] = useState([]);
  const [ layers, setLayers ] = useState([]);
  const [ dialog, setDialog ] = useState({ isOpen: false, data: {}, type: '' });
  // const [ concernLevels, setConcernLevels ] = useState(null);
  const [ natureLevels, setNatureLevels ] = useState(null);
  const [ spatialFootprint, setSpatialFootprint ] = useState({});

  const { setFetchStatus, fetchStatusEnum, isLoading, isSuccess } = useFetchStatus();

  const classes = useStyles({ isLoading });

  const { LOADING, SUCCESS } = fetchStatusEnum;

  useEffect(() => {
    const fetchData = async () => {
      setFetchStatus(LOADING);

      const [ generalInfo, { layers }, { totalProjectCount }, footprintStats, companyInfo ] = await Promise.all([
        projectApi.getSitesInfo(companyId),
        companyApi.getInterestAreas(),
        projectApi.getLatestProjects(),
        companyApi.getFootprintsStatsByCompany(),
        companyApi.getCompanyDashboardInfo(),
      ]);
      if (generalInfo && totalProjectCount) {
        setIndicators({
          siteCount: generalInfo.nSites,
          projectCount: totalProjectCount,
          sitesSurface: companyInfo.sitesSurface,
          projectSurface: 492779.08,
          minConditionProjects: { ajustedArea: 93474.8, variation: 7, comparedTo: 2020 },
          minConditionSites: { ajustedArea: 50057.41, variation: 16.02, comparedTo: 2020 },
        });
      }
      setSpatialFootprint(footprintStats);
      if (layers) {
        setLayers(layers);
      }
      // const concernLevelsData = [
      //   { id: 54, value: 0.2685 },
      //   { id: 105, value: 0.087 },
      //   { id: 55, value: 0.348 },
      //   { id: 102, value: 0.074 },
      //   { id: 4, value: 0.076 },
      // ];
      const natureLevels = {
        naturalVegetationCoverage: {
          MB: 0,
          B: 1,
          M: 0,
          A: 1,
          MA: 0,
        },
        ecosystemIntegrity: {
          MB: 0,
          B: 0,
          M: 0,
          A: 2,
          MA: 3,
        },
        freshwaterQuality: {
          MB: 0,
          B: 0,
          M: 5,
          A: 0,
          MA: 0,
        },
        nativeSpeciesAbundance: {
          MB: 0,
          B: 0,
          M: 0,
          A: 0,
          MA: 0,
        },
        freshwaterAvailability: {
          MB: 0,
          B: 0,
          M: 0,
          A: 0,
          MA: 0,
        },
      };
      setNatureLevels(natureLevels);
      // setConcernLevels(concernLevelsData);
      // setLatestProjects(projects);
      setFetchStatus(SUCCESS);
    };

    if (companyId) {
      fetchData();
    }

    //eslint-disable-next-line
  }, [ companyId ]);

  useEffect(() => {
    document.title = companyName ? `${companyName} - Resumen` : 'Nviro Monitor';
  }, [ companyName ]);

  // const goToProject = project => history.push(`/web/projects/${project.id}`);

  const closeDialog = () => setDialog({ isOpen: false, type: '' });

  const getDialogType = () => {
    switch (dialog.type) {
      case 'photo':
        return <DialogExternalImage
          pictureData={dialog.data.pictureData}
          additionalClass='image-species-container'
        />;
      default:
        break;
    }
  };

  // const hasProjects = latestProjects.length > 0;

  return (
    <Grid container justifyContent="center" className={classes.mainContainer}>
      <TitleContainer>
        {companyName}
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <Grid container spacing={1}>
          <Grid container item direction='column'>
            <Grid item>
              <Paper className={classes.paper}>
                <Grid container spacing={2}>
                  <Grid item xs>
                    <Box className={classes.itemRootBorder} height="88px">
                      <NumberIndicator
                        title="Proyectos"
                        value={indicators.projectCount}
                        isLoading={isLoading}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs>
                    <Box className={classes.itemRootBorder} height="88px">
                      <NumberIndicator
                        title="Sitios"
                        value={indicators.siteCount}
                        isLoading={isLoading}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs>
                    <Box className={classes.itemRootBorder} height="88px">
                      <NumberIndicator
                        title="Superficie Control Operacional"
                        value={`${formatNumber(indicators.projectSurface)} ha`}
                        isLoading={isLoading}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs>
                    <Box height="88px">
                      <NumberIndicator
                        title="Superficies Sitios Prioritarios"
                        value={`${formatNumber(indicators.sitesSurface)} ha`}
                        isLoading={isLoading}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
          <Grid container item xs={6} direction="column">
            <Grid item>
              <Paper className={classes.paper}>
                <Grid container spacing={2}>
                  <Grid item xs>
                    <Box className={classes.itemRootBorder} height="88px">
                      <NumberIndicator
                        title="Condición general Nivel Operacional"
                        value={`${formatNumber(indicators.minConditionProjects.ajustedArea)} ha eq.`}
                        rate={indicators.minConditionProjects.variation}
                        comparisonLabel={indicators.minConditionProjects.comparedTo}
                        isLoading={isLoading}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs>
                    <Box height="88px">
                      <NumberIndicator
                        title="Condición general Nivel Sitios"
                        value={`${formatNumber(indicators.minConditionSites.ajustedArea)} ha eq.`}
                        rate={indicators.minConditionSites.variation}
                        comparisonLabel={indicators.minConditionSites.comparedTo}
                        isLoading={isLoading}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Box mt={1} />
            <Grid item>
              <AnalyticCard title="Proyectos">
                <Divider />
                {isLoading && <Box mt={0.5}><Skeleton animation="wave" variant="rect" height={heightVH(35)} /></Box>}
                <MapWrapper
                  id="sma-summary-map"
                  className={classes.map}
                  options={mapOptions}
                  height={isLoading ? 0 : heightVH(35)}
                  layers={
                    layers.map(amd => buildLeafletLayer({
                      features: amd.geojson,
                      id: amd.id,
                      name: amd.name,
                      color: getRandomNviroColor(),
                    }))
                  }
                  mapRef={mapRef}
                  fitBoundsOnUpdate
                />
              </AnalyticCard>
            </Grid>
            {/*<Box mt={1} />
            <Grid item>
              <AnalyticCard title="Nivel de impacto por proyecto">
                <Divider />
                <Box mt={1} mb={2}>
                  <Typography variant="caption" color="textSecondary" gutterBottom>
                    Resumen de todos tus sitios según nivel de impacto
                  </Typography>
                </Box>
                <Box>
                  {isLoading ? <PieChartSkeleton /> : <ProjectLevelDoughnutChart projectCount={indicators.projectCount} />}
                </Box>
              </AnalyticCard>
            </Grid>*/}
          </Grid>

          <Grid container item xs={6} direction="column">
            {/* <Grid item>
              <AnalyticCard title="Proyectos según nivel de preocupación">
                <Divider />
                <Box my={0.5} />
                {isLoading && <LoadingBox label="proyectos" />}
                {(isSuccess && hasProjects) &&
                  <ProjectsByConcernLevel projects={latestProjects} concernLevels={concernLevels} goToProject={goToProject} />}
              </AnalyticCard>
            </Grid> */}
            {/*<Box mt={1} />
            <Grid item>
              <AnalyticCard title="Nivel de preocupación por proyecto">
                <Divider />
                <Box mt={1} mb={2}>
                  <Typography variant="caption" color="textSecondary" gutterBottom>
                    Resumen de todos tus sitios según nivel de preocupación
                  </Typography>
                </Box>
                <Box>
                  {isLoading ? <PieChartSkeleton /> : <ProjectLevelDoughnutChart projectCount={indicators.projectCount} />}
                </Box>
              </AnalyticCard>
            </Grid>
            <Box mt={1} />*/}
            {<Grid item>
              <AnalyticCard title="Niveles de preocupación en el estado de la naturaleza">
                <Divider />
                {isLoading && <LoadingBox label="niveles de preocupación" />}
                {isSuccess && <ConcernLevelsStateOfNature natureLevels={natureLevels} />}
              </AnalyticCard>
            </Grid>}
            <>
              <Box mt={1} />
              <AnalyticCard title="Huella espacial total">
                <Divider />
                <Box mt={2} />
                {isLoading && <LoadingBox label="huella espacial" />}
                {isSuccess && <SpatialFootprintTable data={spatialFootprint} /> }
              </AnalyticCard>
            </>
          </Grid>
        </Grid>
        <Box my={2} />
      </ContentContainer>
      <DialogWrap maxWidth='sm' fullWidth onClose={closeDialog} aria-labelledby='form-dialog-title'
        open={dialog.isOpen}>
        {getDialogType()}
      </DialogWrap>
    </Grid>
  );
};


export {
  CompanySummaryContainer,
};
